export const PAYMENT_METHOD_OPTIONS = [
	{value: "cash", label: "Cash"},
	{value: "share_link", label: "Share Link"},
	{value: "custom", label: "Custom"},
	{value: "multiple_payment", label: "Multiple Payment"},
];

export const INVOICE_PRODUCT_RELATION_FIELDS = {
	administrations: [{key: "adminPriceId", label: "Admin Price ID", nullable: true}],
	doctorPrices: [{key: "doctorUserId", label: "Doctor User ID", nullable: false}],
	checkupSupports: [
		{key: "checkupSupportHospitalId", label: "Checkup Support Hospital ID", nullable: true},
		{key: "checkupSupportId", label: "Checkup Support ID", nullable: true},
	],
	medicines: [
		{key: "medicineId", label: "Medicine ID", nullable: false},
		{key: "unitId", label: "Unit ID", nullable: false},
		{key: "sediaanId", label: "Sediaan ID", nullable: false},
	],
};

const PAYMENT_METHOD_VALUES = ["cash", "share_link", "custom", "multiple_payment"];

function toStringOrEmpty(value) {
	if (value === null || value === undefined) {
		return "";
	}

	return String(value);
}

function toDateInputValue(value) {
	if (value === null || value === undefined) {
		return "";
	}

	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value.toISOString().slice(0, 10);
	}

	const asString = String(value).trim();

	if (!asString) {
		return "";
	}

	if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
		return asString;
	}

	const prefixDateMatch = asString.match(/^(\d{4}-\d{2}-\d{2})/);

	if (prefixDateMatch?.[1]) {
		return prefixDateMatch[1];
	}

	const parsedDate = new Date(asString);

	if (!Number.isNaN(parsedDate.getTime())) {
		return parsedDate.toISOString().slice(0, 10);
	}

	return asString;
}

export function toPositiveNumber(value) {
	const parsed = Number(value);

	if (!Number.isFinite(parsed)) {
		return 0;
	}

	return parsed;
}

export function createEmptyProductItem(idField) {
	const relationFields = Array.isArray(idField)
		? idField
		: idField
		? [
				{
					key: idField,
				},
		  ]
		: [];

	return relationFields.reduce(
		(item, relationField) => ({
			...item,
			[relationField.key]: "",
		}),
		{
			id: null,
			title: "",
			qty: "",
			price: "",
		},
	);
}

export function createInvoiceInitialFormState() {
	return {
		id: null,
		invoiceNumber: "",
		patientName: "",
		hospitalId: "",
		hospitalName: "",
		date: "",
		paymentMethod: "",
		customPaymentMethodId: "",
		customPaymentMethodName: "",
		cashAmount: "",
		paymentAmount: "",
		additionalPaymentMethod: "",
		administrations: [],
		doctorPrices: [],
		checkupSupports: [],
		medicines: [],
	};
}

function mapProductItems(items = [], idField) {
	if (!Array.isArray(items)) {
		return [];
	}

	const relationFields = Array.isArray(idField)
		? idField
		: idField
		? [
				{
					key: idField,
				},
		  ]
		: [];

	const getRelationValue = (item, relationKey) => {
		const directValue = item?.[relationKey];

		if (directValue !== null && directValue !== undefined) {
			return directValue;
		}

		const nestedObject = item?.[relationKey?.replace(/Id$/, "")];

		if (nestedObject && nestedObject?.id !== undefined && nestedObject?.id !== null) {
			return nestedObject.id;
		}

		return "";
	};

	return items.map(item => ({
		id: item?.id ?? null,
		title: item?.title || item?.name || "",
		qty: toStringOrEmpty(item?.qty ?? item?.quantity),
		price: toStringOrEmpty(item?.price),
		...relationFields.reduce(
			(accumulator, relationField) => ({
				...accumulator,
				[relationField.key]: toStringOrEmpty(getRelationValue(item, relationField.key)),
			}),
			{},
		),
	}));
}

export function mapInvoiceDetailToFormState(apiResponse) {
	const detail = apiResponse?.data || apiResponse || {};
	const paymentMethod = toStringOrEmpty(detail?.paymentMethod);
	const normalizedPaymentMethod = PAYMENT_METHOD_VALUES.includes(paymentMethod) ? paymentMethod : "";
	const additionalPaymentMethod =
		normalizedPaymentMethod === "multiple_payment"
			? "share_link"
			: toStringOrEmpty(detail?.additionalPaymentMethod);

	return {
		...createInvoiceInitialFormState(),
		id: detail?.id ?? null,
		invoiceNumber: detail?.invoiceNumber || "",
		patientName: detail?.patientName || "",
		hospitalId: toStringOrEmpty(detail?.hospitalId ?? detail?.hospital?.id),
		hospitalName: detail?.hospitalName || "",
		date: toDateInputValue(detail?.date),
		paymentMethod: normalizedPaymentMethod,
		customPaymentMethodId: toStringOrEmpty(detail?.customPaymentMethodId),
		customPaymentMethodName: toStringOrEmpty(detail?.customPaymentMethod?.name ?? detail?.customPaymentMethodName),
		cashAmount: toStringOrEmpty(detail?.cashAmount),
		paymentAmount: toStringOrEmpty(detail?.paymentAmount),
		additionalPaymentMethod,
		administrations: mapProductItems(detail?.administrations, INVOICE_PRODUCT_RELATION_FIELDS.administrations),
		doctorPrices: mapProductItems(detail?.doctorPrices, INVOICE_PRODUCT_RELATION_FIELDS.doctorPrices),
		checkupSupports: mapProductItems(detail?.checkupSupports, INVOICE_PRODUCT_RELATION_FIELDS.checkupSupports),
		medicines: mapProductItems(detail?.medicines, INVOICE_PRODUCT_RELATION_FIELDS.medicines),
	};
}

function normalizeRelationValue(value, nullable) {
	if (value === "" || value === undefined || value === null) {
		return nullable ? null : "";
	}

	if (typeof value === "number") {
		return value;
	}

	if (typeof value === "string") {
		const trimmed = value.trim();

		if (!trimmed) {
			return nullable ? null : "";
		}

		if (/^-?\d+$/.test(trimmed)) {
			return Number(trimmed);
		}

		return trimmed;
	}

	return value;
}

function mapProductPayload(items = [], relationFields = []) {
	if (!Array.isArray(items)) {
		return [];
	}

	return items
		.filter(item => {
			const hasRelationFieldValue = relationFields.some(relationField => item?.[relationField.key] !== "");

			return item?.title || item?.qty || item?.price || hasRelationFieldValue;
		})
		.map(item => {
			const mappedItem = {
				title: (item?.title || "").trim(),
				qty: toPositiveNumber(item?.qty),
				price: toPositiveNumber(item?.price),
			};

			if (item?.id) {
				mappedItem.id = item.id;
			}

			relationFields.forEach(relationField => {
				mappedItem[relationField.key] = normalizeRelationValue(
					item?.[relationField.key],
					relationField.nullable,
				);
			});

			return mappedItem;
		});
}

export function mapFormStateToUpdatePayload(formState) {
	const normalizedDate = toDateInputValue(formState?.date);
	const normalizedPaymentMethod =
		typeof formState?.paymentMethod === "string" ? formState.paymentMethod.trim() : formState?.paymentMethod;

	const payload = {
		date: normalizedDate,
		paymentMethod: normalizedPaymentMethod,
		administrations: mapProductPayload(formState?.administrations, INVOICE_PRODUCT_RELATION_FIELDS.administrations),
		doctorPrices: mapProductPayload(formState?.doctorPrices, INVOICE_PRODUCT_RELATION_FIELDS.doctorPrices),
		checkupSupports: mapProductPayload(formState?.checkupSupports, INVOICE_PRODUCT_RELATION_FIELDS.checkupSupports),
		medicines: mapProductPayload(formState?.medicines, INVOICE_PRODUCT_RELATION_FIELDS.medicines),
	};

	if (normalizedPaymentMethod === "custom") {
		payload.customPaymentMethodId = normalizeRelationValue(formState?.customPaymentMethodId, false);
	}

	if (normalizedPaymentMethod === "multiple_payment") {
		payload.cashAmount = toPositiveNumber(formState?.cashAmount);
		payload.additionalPaymentMethod = "share_link";

		if (toPositiveNumber(formState?.paymentAmount) > 0) {
			payload.paymentAmount = toPositiveNumber(formState?.paymentAmount);
		}
	}

	return payload;
}

export function calculateSectionSubtotal(items = []) {
	if (!Array.isArray(items) || !items.length) {
		return 0;
	}

	return items.reduce((sum, item) => {
		const qty = toPositiveNumber(item?.qty);
		const price = toPositiveNumber(item?.price);

		return sum + qty * price;
	}, 0);
}

export function calculateGrandTotal(formState) {
	return (
		calculateSectionSubtotal(formState?.administrations) +
		calculateSectionSubtotal(formState?.doctorPrices) +
		calculateSectionSubtotal(formState?.checkupSupports) +
		calculateSectionSubtotal(formState?.medicines)
	);
}

export function getApiErrorMessage(error) {
	const responseData = error?.response?.data;
	const fallbackMessage = "Terjadi kesalahan saat memproses request";

	if (typeof responseData?.message === "string") {
		if (Array.isArray(responseData?.errors) && responseData.errors.length) {
			const validationMessages = responseData.errors
				.map(item => item?.msg || item?.message || item?.error)
				.filter(Boolean);

			if (validationMessages.length) {
				return `${responseData.message}: ${validationMessages.join(", ")}`;
			}
		}

		return responseData.message;
	}

	if (Array.isArray(responseData?.errors) && responseData?.errors?.length) {
		const validationMessages = responseData.errors
			.map(item => item?.msg || item?.message || item?.error)
			.filter(Boolean);

		if (validationMessages.length) {
			return validationMessages.join(", ");
		}
	}

	return fallbackMessage;
}
